import { useLayoutMode } from "@/hooks/useLayoutMode";
import { useAuthContext } from "@/context/AuthContext";
import { useParentAudience } from "@/hooks/useParentAudience";
import { getSubscriptionCtaTarget } from "@/lib/authRedirect";
import { usesLoginCheckout } from "@/lib/abonnementEmailCheckoutExperiment";
import { useRouter } from "next/navigation";
import { useCallback } from "react";

interface SubscriptionCTAProps {
  parentRegistration?: boolean;
  className?: string;
  onClick?: () => void;
  onBeforeNavigate?: () => void;
  variant?: "filled" | "outline";
}

export default function SubscriptionCTA({
  parentRegistration,
  className,
  onClick,
  onBeforeNavigate,
  variant = "filled"
}: SubscriptionCTAProps) {
  const { layoutMode } = useLayoutMode();
  const { userData, isLoggedIn } = useAuthContext();
  const isParent = useParentAudience();
  const router = useRouter();

  const hasUsedTrial = Boolean(isLoggedIn && userData?.trial_expired);

  const handleClick = useCallback(async () => {
    onBeforeNavigate?.();
    const loginCheckout = await usesLoginCheckout(layoutMode === "native", isLoggedIn);
    router.push(getSubscriptionCtaTarget({ isLoggedIn, parentRegistration, native: loginCheckout }));
  }, [onBeforeNavigate, parentRegistration, isLoggedIn, layoutMode, router]);

  let text: string;
  if (parentRegistration || isParent) {
    text = hasUsedTrial
      ? "Abonnement afsluiten"
      : "Start proefweek voor je kind";
  } else if (hasUsedTrial) {
    text = "Abonnement afsluiten";
  } else {
    text = "Start proefweek";
  }

  const baseClasses =
    "font-bold text-base py-3 px-6 rounded-lg transition-colors cursor-pointer";
  const variantClasses =
    variant === "outline"
      ? "border-2 border-mathyellow text-gray-900 bg-transparent hover:bg-mathyellow/10"
      : "bg-mathyellow text-gray-900 hover:bg-mathyellow/90";

  return (
    <button
      onClick={onClick ?? handleClick}
      className={`${baseClasses} ${variantClasses} ${className ?? ""}`}
    >
      {text}
    </button>
  );
}
